import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Trophy, Users, Calendar, Flame, CheckCircle2, ImagePlus, X as XIcon, MapPin, Pencil } from "lucide-react";
import MapEmbed, { extractEmbedSrc } from "../../components/MapEmbed";
import { ddmmyyyy } from "../../lib/dates";

const SPORTS = ["Football", "Box Cricket", "Badminton", "Basketball", "Pickleball", "Padel"];
const MAX_PHOTOS = 15;
const MAX_BYTES = 10 * 1024 * 1024;

export default function AdminEvents() {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [centres, setCentres] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const EMPTY_FORM = {
        title: "", sport: "Football", centre_id: "", description: "",
        start_date: "", end_date: "", registration_fee: 2000, max_teams: 16,
        photo_url: "", photos: [],
        rules: "", prize: "", active: true,
        venue_name: "", venue_address: "", map_embed_url: "",
    };
    const [form, setForm] = useState(EMPTY_FORM);

    const fetchEvents = async () => {
        const { data } = await api.get("/events", { params: { active_only: false } });
        setEvents(data);
    };
    useEffect(() => {
        fetchEvents();
        api.get("/centres").then(({ data }) => {
            setCentres(data);
            if (data.length) setForm(f => ({ ...f, centre_id: f.centre_id || data[0].id }));
        });
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, centre_id: centres[0]?.id || "" });
        setOpen(true);
    };

    const openEdit = (e) => {
        setEditingId(e.id);
        setForm({
            title: e.title || "",
            sport: e.sport || "Football",
            centre_id: e.centre_id || "",
            description: e.description || "",
            start_date: e.start_date || "",
            end_date: e.end_date || "",
            registration_fee: e.registration_fee ?? 2000,
            max_teams: e.max_teams ?? 16,
            photo_url: e.photo_url || "",
            photos: e.photos || [],
            rules: e.rules || "",
            prize: e.prize || "",
            active: e.active !== false,
            venue_name: e.venue_name || "",
            venue_address: e.venue_address || "",
            map_embed_url: e.map_embed_url || "",
        });
        setOpen(true);
    };

    const handleFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        if (form.photos.length + arr.length > MAX_PHOTOS) {
            toast.error(`Max ${MAX_PHOTOS} photos. You already have ${form.photos.length}.`);
            return;
        }
        setUploading(true);
        const newUrls = [];
        for (const f of arr) {
            if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) {
                toast.error(`${f.name}: only PNG / JPEG allowed`);
                continue;
            }
            if (f.size > MAX_BYTES) { toast.error(`${f.name}: exceeds 10 MB`); continue; }
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                newUrls.push(data.url);
            } catch (e) {
                toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
            }
        }
        if (newUrls.length) {
            setForm(prev => ({
                ...prev,
                photos: [...prev.photos, ...newUrls],
                photo_url: prev.photo_url || newUrls[0],
            }));
            toast.success(`Uploaded ${newUrls.length} photo${newUrls.length === 1 ? "" : "s"}`);
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const removePhoto = (url) => setForm({
        ...form,
        photos: form.photos.filter(p => p !== url),
        photo_url: form.photo_url === url ? (form.photos.find(p => p !== url) || "") : form.photo_url,
    });

    const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/events/${editingId}`, form);
                toast.success("Event updated");
            } else {
                await api.post("/events", form);
                toast.success("Event created");
            }
            setOpen(false);
            setEditingId(null);
            setForm({ ...EMPTY_FORM, centre_id: centres[0]?.id || "" });
            fetchEvents();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete event and all registrations?")) return;
        await api.delete(`/events/${id}`);
        toast.success("Deleted");
        fetchEvents();
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Events</h1>
                        <p className="text-slate-400 text-sm mt-1">Create and manage tournaments.</p>
                    </div>
                    <button onClick={openCreate} data-testid="new-event-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> New Event
                    </button>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {events.map(e => (
                        <div key={e.id} onClick={() => navigate(`/admin/events/${e.id}`)}
                            className="bg-slate-900 border border-slate-800 rounded-lg p-5 relative cursor-pointer hover:border-[#00FF66]/50 transition-colors"
                            data-testid={`admin-event-${e.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{e.sport}</span>
                                        {e.is_completed && (
                                            <span data-testid={`event-completed-${e.id}`}
                                                className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-slate-700 text-slate-200 border border-slate-600">
                                                <CheckCircle2 className="w-3 h-3" /> Completed
                                            </span>
                                        )}
                                        {!e.is_completed && e.few_spots_left && (
                                            <span data-testid={`event-fewspots-${e.id}`}
                                                className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                                <Flame className="w-3 h-3" /> Few spots left
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-display text-lg font-bold uppercase text-white mt-2 truncate">{e.title}</h3>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} data-testid={`edit-event-${e.id}`}
                                        className="text-slate-400 hover:text-[#00FF66] transition-colors" title="Edit event">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} data-testid={`del-event-${e.id}`}
                                        className="text-slate-500 hover:text-red-400">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-400">
                                <div className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-[#00FF66]" />{ddmmyyyy(e.start_date)}</div>
                                <div className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-[#00FF66]" />{e.registrations_count}/{e.max_teams}</div>
                                <div className="inline-flex items-center gap-1"><Trophy className="w-3 h-3 text-[#00FF66]" />₹{e.registration_fee}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {open && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <form onSubmit={save} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="event-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editingId ? "Edit Event" : "New Event"}</h3>
                            <Input label="Title" value={form.title} onChange={v => setForm({ ...form, title: v })} testid="event-title" />
                            <div className="grid grid-cols-2 gap-3">
                                <Select label="Sport" value={form.sport} onChange={v => setForm({ ...form, sport: v })} options={SPORTS} testid="event-sport" />
                                <Select label="Centre" value={form.centre_id} onChange={v => setForm({ ...form, centre_id: v })}
                                    options={centres.map(c => ({ value: c.id, label: c.name }))} testid="event-centre" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="Start Date" type="date" value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} testid="event-start" />
                                <Input label="End Date" type="date" value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} testid="event-end" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="Reg. Fee" type="number" value={form.registration_fee} onChange={v => setForm({ ...form, registration_fee: parseInt(v) || 0 })} testid="event-fee" />
                                <Input label="Max Teams" type="number" value={form.max_teams} onChange={v => setForm({ ...form, max_teams: parseInt(v) || 0 })} testid="event-max" />
                            </div>
                            <Input label="Prize" value={form.prize} onChange={v => setForm({ ...form, prize: v })} testid="event-prize" optional />

                            {/* Mod 4 — Event venue with map embed URL (no API key needed) */}
                            <div className="border border-slate-800 rounded p-3 space-y-3" data-testid="event-venue-section">
                                <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 inline-flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-[#00FF66]" /> Event Venue Location
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Input label="Venue Name" value={form.venue_name} onChange={v => setForm({ ...form, venue_name: v })} testid="event-venue-name" optional />
                                    <Input label="Venue Address" value={form.venue_address} onChange={v => setForm({ ...form, venue_address: v })} testid="event-venue-address" optional />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">Map Embed URL <span className="text-slate-500 normal-case tracking-normal ml-1">optional</span></label>
                                    <p className="text-[11px] text-slate-500 mt-1">
                                        Open <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="text-[#00FF66] underline">Google Maps</a>, search the venue,
                                        click <b className="text-slate-300">Share → Embed a map → Copy HTML</b>. Paste the snippet or just the URL. Leave blank to fall back to the centre&apos;s map.
                                    </p>
                                    <textarea value={form.map_embed_url}
                                        onChange={(e) => setForm({ ...form, map_embed_url: e.target.value })}
                                        placeholder='https://www.google.com/maps/embed?pb=...   OR   <iframe src="https://www.google.com/maps/embed?pb=..." ...></iframe>'
                                        rows={2} data-testid="event-map-url-input"
                                        className="mt-1 w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#00FF66] resize-y" />
                                    {form.map_embed_url && form.map_embed_url.includes("google.com/maps") && (
                                        <div className="mt-2">
                                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Preview</div>
                                            <MapEmbed
                                                url={extractEmbedSrc(form.map_embed_url)}
                                                height={200}
                                                showDirections={false}
                                                testid="event-map-preview"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                                    Event Photos <span className="text-slate-500 normal-case tracking-normal ml-2">{form.photos.length} / {MAX_PHOTOS}</span>
                                </label>
                                <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
                                    className="mt-1 rounded border-2 border-dashed border-slate-700 hover:border-[#00FF66]/50 transition p-4 text-center cursor-pointer"
                                    onClick={() => fileRef.current?.click()}
                                    data-testid="event-photo-dropzone">
                                    <ImagePlus className="w-6 h-6 mx-auto text-slate-500" />
                                    <div className="text-xs text-slate-400 mt-1">Drag & drop or <span className="text-[#00FF66]">click to upload</span></div>
                                    <div className="text-[10px] text-slate-500">PNG / JPEG · up to 10 MB · max {MAX_PHOTOS}</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">Recommended cover: <b className="text-slate-300">1920×700 px</b> / mobile <b className="text-slate-300">1080×1350 px</b>. Any size accepted — auto-cropped per device.</div>
                                    <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden"
                                        onChange={(e) => handleFiles(e.target.files)} data-testid="event-photo-input" />
                                </div>
                                {uploading && <div className="text-[10px] text-amber-400 mt-1">Uploading…</div>}
                                {form.photos.length > 0 && (
                                    <>
                                        <div className="mt-3 text-[11px] text-slate-500">
                                            Click any photo to set it as the cover. The cover is shown on the public events list.
                                        </div>
                                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
                                            {form.photos.map((p) => {
                                                const isCover = form.photo_url === p;
                                                return (
                                                    <button type="button" key={p}
                                                        onClick={() => setForm(prev => ({ ...prev, photo_url: p }))}
                                                        title={isCover ? "Current cover" : "Click to set as cover"}
                                                        data-testid={`event-photo-thumb-${p.split('/').pop()}`}
                                                        className={`relative aspect-video bg-slate-800 rounded overflow-hidden border-2 transition group ${isCover ? "border-[#00FF66] ring-2 ring-[#00FF66]/40" : "border-slate-700 hover:border-[#00FF66]/60"}`}>
                                                        <img src={p} alt="" className="w-full h-full object-cover" />
                                                        <span onClick={(ev) => { ev.stopPropagation(); removePhoto(p); }}
                                                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-950/80 text-slate-300 hover:bg-red-500 hover:text-white flex items-center justify-center cursor-pointer"
                                                            data-testid={`event-photo-remove-${p.split('/').pop()}`}>
                                                            <XIcon className="w-3 h-3" />
                                                        </span>
                                                        {isCover ? (
                                                            <div className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest font-bold bg-[#00FF66] text-black px-1.5 rounded">Cover</div>
                                                        ) : (
                                                            <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/55 transition-colors flex items-center justify-center">
                                                                <span className="opacity-0 group-hover:opacity-100 text-[9px] uppercase tracking-widest font-bold bg-[#00FF66]/90 text-black px-1.5 py-0.5 rounded transition-opacity">
                                                                    Set as cover
                                                                </span>
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Description</label>
                                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3}
                                    className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Rules</label>
                                <textarea value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} rows={2}
                                    className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" data-testid="save-event-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">{editingId ? "Update" : "Save"}</button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Input({ label, type = "text", value, onChange, testid, optional = false }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}{optional && <span className="ml-2 normal-case tracking-normal text-slate-500">(optional)</span>}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={!optional} data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
        </div>
    );
}

function Select({ label, value, onChange, options, testid }) {
    const opts = options.map(o => typeof o === "string" ? { value: o, label: o } : o);
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]">
                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
